import { DealStatusBadge } from '@/components/deal/status/deal-status-badge';
import { Button } from '@/components/ui/button';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { PDKEmptyValueIcon } from '@/components/ui/pdk-icons';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId, useDealsForHub } from '@/hooks/use-deals-selectors';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';
import type { Trade } from '@/models/types';
import { useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';

const NO_DEAL_ASSOCIATED_VALUE = 'No deal associated';

export const getStatusOptions = ({
  deals,
  // className,
}: {
  deals: Deal[];
  // className: string;
}): Option<Deal['short_id']>[] => {
  return [
    {
      value: NO_DEAL_ASSOCIATED_VALUE,
      label: NO_DEAL_ASSOCIATED_VALUE,
    },
    ...deals.map((deal) => ({
      value: `${deal.id} - ${deal.short_id} - ${deal.primary_customer?.full_name}`,
      label: `${deal.short_id} - ${deal.primary_customer?.full_name}`,
      // icon: <DealStatusBadge value={deal.status} className={className} />,
    })),
  ];
};

export type DealStatusSelectorProps = {
  // id: TradeInAppraisal['id'];
  dealId: Trade['deal_id'];
  // hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  // buttonVariant?: 'outline' | 'ghost';
  // className?: string;
  handleDealChange: (option: string) => void;
};

export function DealSelector({
  // id,
  dealId,
  // hideCopy = false,
  side = 'left',
  // DEV UX NOTE: do not turn `withHotkeyListener` in any list or board views
  // because it will lag the page. This is purely meant for limited number of
  // DealStatusSelectors on a page.
  withHotkeyListener = false,
  // buttonVariant = 'ghost',
  // className,
  handleDealChange,
}: DealStatusSelectorProps) {
  console.log('dealId', dealId);
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const deals = useDealsForHub();
  const value = deal?.short_id || NO_DEAL_ASSOCIATED_VALUE;

  const statusButtonRef = useRef<HTMLButtonElement>(null);

  const DEAL_STATUS_OPTIONS_MAP = getStatusOptions({
    deals,
    // className: 'h-3 w-3',
  });

  // "/api/trade-in-appraisals/dbb96c9e-5e1b-4251-90af-e3c1596a126b/link-to-deal/00988684-8d69-4ca2-878b-0ee4f748b4a9"
  // /api/trades/:trade_id/link-to-deal/:deal_id
  // const updateDealAssociatedMutation = useMutation({
  //   onMutate: async ({ dealId }: { dealId: Deal['id'] }) => {
  //     tradesStore.updateTrade(id, {
  //       deal_id: dealId,
  //     });
  //   },
  //   mutationFn: async ({ dealId }: { dealId: Deal['id'] }) => {
  //     const response = await pdkAxios.post(
  //       `/trades/${id}/link-to-deal/${dealId}`,
  //     );
  //     return response.data;
  //   },
  //   onSuccess: () => {},
  //   onError: (error, variables) => {
  //     console.error('Error updating deal associated', error, variables);
  //   },
  //   onSettled: () => {},
  // });

  // const handleDealChange = (option: string) => {
  //   const newDealId = option.split(' - ')[0];
  //   updateDealAssociatedMutation.mutate({ dealId: newDealId });
  // };

  return (
    <div>
      <ComboboxPopover
        endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_STATUS}</KeyboardKeyIcon>}
        placeholder="Set deal..."
        onChange={handleDealChange}
        emptyMessage="No deal found"
        options={DEAL_STATUS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = DEAL_STATUS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleDealChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <Button variant="ghost" className="p-1 h-fit">
          <div
            // DEV NOTE: without this div, combobox-popover won't work
            // that is, clicks will not open the combobox
            // I think the reason is children node needs to be able to receive a ref
            // in order to detect a click event, which makes it open
            // thus so this div is a workaround
            className="flex flex-row gap-2 items-center"
          >
            {/* <TradeStatusBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          /> */}
            {deal && <DealStatusBadge value={deal.status} hideCopy />}
            {value === NO_DEAL_ASSOCIATED_VALUE && (
              <div className="flex flex-row gap-2 items-center pl-2">
                <PDKEmptyValueIcon className="h-3.5 w-3.5" />
              </div>
            )}
            <p className="rounded text-xs  text-gray-600">{value}</p>
          </div>
        </Button>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.DEAL_STATUS}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
